import React, { InputHTMLAttributes } from 'react'
import { createPortal } from 'react-dom'
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'
import { $DCprimaryColor, $DCprimaryShadowColor } from '../styleGuide'
import { colors } from '../styleGuide'

export interface DcDropDownProps extends InputHTMLAttributes<HTMLDivElement> {
  className?: string
  options?: OptionsData[]
  error?: string
  isgrouped?: boolean
  groupOptionData?: any
  placeholder?: string
  onChange: any
  required?: any
  isShowLabel?: boolean
  disabled?: boolean
  customLabel?: string
  label?: string
  dropdownSize?: string
  value?: string | number // Make value optional but controlled
}

interface OptionsData {
  value: string
  label: string
  disabled?: boolean
}

const getGroupedValue = (options, value) => {
  let groupedValue = ''
  Object.keys(options).forEach((group) => {
    options[group].forEach((option) => {
      if (option.value === value) {
        groupedValue = option
      }
    })
  })
  return groupedValue
}

const getValue = (options, value, isgrouped) => {
  const filteredValue = isgrouped
    ? getGroupedValue(options, value)
    : options.find((option) => option.value == value)

  return filteredValue ? filteredValue.label : ''
}

// for search from single options
const filterData = (options, filterText) => {
  if (filterText) {
    return options.filter((option) =>
      option?.label?.toLowerCase()?.includes(filterText?.toLowerCase()),
    )
  }
  return options
}

// for search from grouped options
const filterGroupedData = (options, filterText) => {
  if (filterText) {
    return Object.keys(options).reduce((acc, group) => {
      acc[group] = options[group].filter((option) =>
        option.label.toLowerCase().includes(filterText.toLowerCase()),
      )
      return acc
    }, {})
  }
  return options
}

export const DcDropDown = React.forwardRef<HTMLDivElement, DcDropDownProps>(
  (
    {
      className,
      options = [],
      error = '',
      groupOptionData = {},
      isgrouped = false,
      placeholder = '',
      value, // Don't provide default here
      required = false,
      isShowLabel = true,
      disabled = false,
      customLabel = '',
      label = '',
      onChange = () => {},
      dropdownSize,
      ...rest
    },
    ref,
  ) => {
    // Ensure value is always controlled - never undefined
    const controlledValue = value ?? ''

    const [isOptionsOpen, setIsOptionsOpen] = React.useState(false)
    const [filterText, setFilterText] = React.useState('')
    const [position, setPosition] = React.useState({
      bottom: 0,
      height: 0,
      left: 0,
      right: 0,
      top: 0,
      width: 0,
      x: 0,
      y: 0,
    })
    const [dropdownPosition, setDropdownPosition] = React.useState({
      top: '',
      left: '-5000px',
      width: '',
    })
    const [showLabel, setShowLabel] = React.useState<any>(false)

    const toggleOptions = () => {
      setIsOptionsOpen(!isOptionsOpen)
      handleMouseMove()
    }

    React.useEffect(() => {
      const Value = getValue(options, controlledValue, isgrouped)

      if (placeholder === Value) {
        setShowLabel(false)
      } else {
        if (Value || customLabel !== '') {
          setShowLabel(true)
        } else if (Value === '' || Value === null || Value === undefined) {
          setShowLabel(false)
        }
      }
    }, [
      getValue(options, controlledValue, isgrouped),
      placeholder,
      customLabel,
    ])

    const toggleRef = React.useRef(null)
    const inputRef = React.useRef(null)
    const dropdownRef = React.useRef(null)

    const requriedHeight = () => {
      const windowheight = window.innerHeight
      const dropdownHeight = dropdownRef?.current?.offsetHeight
      const dropdownToTop = position?.top
      const neededHeight = windowheight - dropdownToTop - dropdownHeight
      return neededHeight
    }

    const groupData = filterGroupedData(groupOptionData, filterText)

    React.useEffect(() => {
      const height = requriedHeight()
      const topPosition =
        height > 0
          ? `${position.top + position.height + 1}px`
          : `${position.top - dropdownRef?.current?.offsetHeight + 1}px`

      if (isOptionsOpen) {
        setDropdownPosition({
          top: topPosition,
          left: `${position?.left === 0 ? '0' : position?.left}px`,
          width: `${position?.width}px`,
        })
      } else {
        setDropdownPosition({
          top: '',
          left: '-5000px',
          width: '',
        })
      }
    }, [position, filterText, isOptionsOpen])

    const handleClickOutside = (e: any) => {
      if (toggleRef?.current?.contains(e.target)) {
        return
      }
      if (dropdownRef?.current?.contains(e.target)) {
        return
      }
      setIsOptionsOpen(false)
      setDropdownPosition({
        ...dropdownPosition,
        left: '-5000px',
      })
    }

    React.useEffect(() => {
      const parent = toggleRef.current?.closest('form')
      if (parent) {
        parent.addEventListener('submit', async (e) => {
          const val = toggleRef.current.getAttribute('data-value')

          if (`${val}`.length === 0) {
            e.preventDefault()
            parent.reportValidity()
          }
        })
      }

      document.addEventListener('click', handleClickOutside)
      return () => {
        document.removeEventListener('click', handleClickOutside)
      }
    }, [controlledValue]) // Use controlledValue instead of value

    const handleMouseMove = () => {
      if (toggleRef?.current) {
        const rect = toggleRef.current.getBoundingClientRect()
        setPosition(rect)
      }
    }

    React.useEffect(() => {
      const handleScroll = () => {
        handleMouseMove()
      }

      const handleResize = () => {
        handleMouseMove()
      }

      document.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleResize)

      return () => {
        document.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleResize)
      }
    }, [])

    const handleWindowScroll = () => {
      setIsOptionsOpen(false)
      setDropdownPosition({
        ...dropdownPosition,
        left: '-5000px',
      })
    }

    React.useEffect(() => {
      window.addEventListener('scroll', handleWindowScroll)
      return () => {
        window.removeEventListener('scroll', handleWindowScroll)
      }
    }, [])

    // Portal dropdown content
    const dropdownContent = isOptionsOpen && (
      <DropDownListPortal
        ref={dropdownRef}
        display={isOptionsOpen ? 'flex' : 'none'}
        position={dropdownPosition}
        inputPosition={requriedHeight() < 0}
      >
        {isgrouped ? (
          <React.Fragment>
            {options && options?.length > 5 && (
              <SearchPixelInput>
                <Search
                  placeholder="Search"
                  name="search"
                  onChange={(e) => setFilterText(e.target.value)}
                  value={filterText}
                />
              </SearchPixelInput>
            )}
            <OptionalContainer>
              {Object.keys(groupData).map((key) => {
                if (groupData[key].length == 0) return null

                return (
                  <OptGroup label={key} key={key}>
                    {groupData[key].map((option, index) => {
                      return (
                        <Options
                          onClick={() => {
                            onChange &&
                              onChange({
                                target: {
                                  value: option.value,
                                  options: [{ text: option.label }],
                                  selectedIndex: 0,
                                },
                              })
                            setIsOptionsOpen(false)
                            setDropdownPosition({
                              ...dropdownPosition,
                              left: '-5000px',
                            })
                          }}
                          key={index}
                          value={option.value}
                          disabled={option.disabled}
                          className={
                            option.value === controlledValue ? 'selected' : ''
                          }
                        >
                          {option.label}
                        </Options>
                      )
                    })}
                  </OptGroup>
                )
              })}
            </OptionalContainer>
          </React.Fragment>
        ) : (
          <React.Fragment>
            {options && options?.length > 5 && (
              <SearchPixelInput>
                <InputContainer>
                  <Search
                    placeholder="Search"
                    name="search"
                    onChange={(e) => setFilterText(e.target.value)}
                    value={filterText}
                  />
                  {filterText?.length > 0 && (
                    <React.Fragment>
                      {filterData(options, filterText)?.length === 0 && (
                        <NoFoundError>No Data Found</NoFoundError>
                      )}
                    </React.Fragment>
                  )}
                </InputContainer>
              </SearchPixelInput>
            )}

            <OptionalContainer>
              {options && options.length > 0 ? (
                <React.Fragment>
                  {filterData(options, filterText)?.map((option, index) => {
                    return (
                      <Option
                        key={index}
                        className={
                          option.value === controlledValue ? 'selected' : ''
                        }
                        value={option.value}
                        disabled={option.disabled}
                        onClick={() => {
                          onChange &&
                            onChange({
                              target: {
                                value: option.value,
                                options: [{ text: option.label }],
                                label: option.label,
                                selectedIndex: 0,
                              },
                            })
                          setIsOptionsOpen(false)
                          setDropdownPosition({
                            ...dropdownPosition,
                            left: '-5000px',
                          })
                        }}
                      >
                        {option.label}
                      </Option>
                    )
                  })}
                </React.Fragment>
              ) : (
                <Option>No data available</Option>
              )}
            </OptionalContainer>
          </React.Fragment>
        )}
      </DropDownListPortal>
    )

    return (
      <Mainconatiner>
        {label && (
          <Label>
            {label}
            {required && <Required>*</Required>}
          </Label>
        )}
        <HiddenInput
          type="text"
          ref={inputRef}
          value={controlledValue} // Use controlledValue to ensure it's never undefined
          required={required}
          onChange={() => {}}
        />

        <DropDown
          ref={toggleRef}
          {...rest}
          className={className}
          data-value={controlledValue} // Use controlledValue
        >
          <Toggler
            isShowError={error !== ''}
            dropdownSize={dropdownSize}
            disable={disabled}
            onClick={() => {
              if (disabled) {
                return
              }
              toggleOptions()
            }}
          >
            <OptionLabel
              color={
                isgrouped
                  ? colors.black
                  : showLabel
                  ? ''
                  : colors.dropdownGrayLight
              }
            >
              {getValue(
                isgrouped ? groupOptionData : options,
                controlledValue, // Use controlledValue
                isgrouped,
              ) || placeholder}
            </OptionLabel>
            {isOptionsOpen ? (
              <FontAwesomeIcon
                color={colors.dropdownGray}
                width={'10px'}
                icon={faAngleUp}
              />
            ) : (
              <FontAwesomeIcon
                color={colors.dropdownGray}
                width={'10px'}
                icon={faAngleDown}
              />
            )}
          </Toggler>
        </DropDown>

        {/* Render dropdown in portal to avoid positioning issues */}
        {typeof document !== 'undefined' &&
          createPortal(dropdownContent, document.body)}

        {error !== '' && <Error>{error}</Error>}
      </Mainconatiner>
    )
  },
)

// Styled Components
const Mainconatiner = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  /* gap: 3px; */
`

const Toggler = styled.button.attrs({ type: 'button', isShowError: false })<{
  dropdownSize: string
  disable: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  &:focus {
    border-color: ${(props) =>
      props.isShowError ? colors.danger : colors.dropdownBorder};
    box-shadow: 0 0 0 1px
      ${(props) => (props.isShowError ? colors.dangerLight : $DCprimaryColor)};
  }
  background-color: ${colors.white};
  border: 1px solid ${colors.dropdownBorder};
  border-radius: 0.375rem;
  display: inline-flex;
  flex-wrap: wrap;
  font-size: 0.875rem;
  padding: 0.5625rem 18px;
  min-height: 43px;
  ${(props) =>
    props.dropdownSize === 'sm' &&
    'min-height: 30px; max-height: 30px; padding: 3px 0.75rem;'}
  overflow: hidden;
  width: 100%;
  cursor: ${(props) => (props.disable ? 'not-allowed' : 'pointer')};
  background: ${(props) =>
    props.disable ? colors.dropdownGrayVeryLight : 'none'};
`

const DropDown = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  cursor: pointer;
  &:last-child {
    margin-bottom: 0;
  }
`

const OptionalContainer = styled.div`
  width: 100%;
  max-height: 250px;
  overflow: auto;
`

const OptionLabel = styled.div<{ color?: string }>`
  color: ${(props) => (props.color ? props.color : 'black')};
`

// Updated DropDownList for Portal - higher z-index and better positioning
const DropDownListPortal = styled.div<{
  position?: any
  display: string
  inputPosition?: boolean
}>`
  display: ${(props) => props.display};
  flex-direction: ${(props) =>
    props.inputPosition ? 'column-reverse' : 'column'};
  opacity: ${(props) => (props.display === 'flex' ? 1 : 0)};
  background-color: ${colors.white};
  width: ${(props) => props.position.width};
  position: fixed;
  top: ${(props) => props.position?.top};
  left: ${(props) => props.position?.left};
  background-color: ${colors.white};
  border-radius: 0.375rem;
  box-shadow: 0 2px 12px -1px rgba(67, 71, 107, 0.2);
  overflow: hidden;
  will-change: visibility;
  word-break: break-all;
  z-index: 999999; /* Very high z-index for portal */
  pointer-events: ${(props) => (props.display === 'flex' ? 'auto' : 'none')};
`

const Option = styled.div`
  background-color: ${colors.white};
  height: 40px;
  padding: 5px 5px 5px 25px;
  max-width: 100% !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  &:hover {
    background-color: ${colors.blueLightBg};
  }
  &.selected {
    background-color: ${colors.dropdownBlue};
    color: ${colors.white};
  }
`

const Options = styled.div`
  background-color: ${colors.white};
  height: 40px;
  padding: 5px 5px 5px 25px;
  max-width: 100% !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  &:hover {
    background-color: ${colors.blueLightBg};
  }
  &.selected {
    background-color: ${colors.dropdownBlue};
    color: ${colors.white};
  }
`

const OptGroup = styled.div`
  width: 100%;
  border-top: 1px solid ${colors.dropdownBorderLight};
  padding: 0.375rem 0.75rem;
  font-weight: bold;
  font-size: 12px;
  color: ${colors.dropdownGrayMedium};
  background-color: ${colors.dropdownGrayLighter};
`

const InputContainer = styled.div`
  position: relative;
`

const NoFoundError = styled.div`
  font-size: 11px;
  position: absolute;
  top: 21px;
  right: 6px;
  color: red;
`

const SearchPixelInput = styled.div`
  padding: 0.375rem 0.75rem;
  box-sizing: border-box;
  width: 100%;
  top: 0;
  left: 0;
`

const Search = styled.input`
  background-color: ${colors.white} !important;
  width: 100%;
  border: 1px solid ${colors.dropdownBorderMedium};
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;

  &:focus,
  &:active,
  &:focus-visible {
    outline: none;
  }
`

const Error = styled.span`
  font-size: 90%;
  color: rgb(255 0 0 / 64%);
`

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${colors.grayTextDark};
  text-transform: none;
`

const Required = styled.span`
  color: ${colors.danger};
  margin-left: 0.25rem;
`

const HiddenInput = styled.input`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.375rem 0.75rem;
  border: 1px solid ${colors.dropdownBorderLight};
  border-radius: 0.25rem;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  opacity: 0;
`

export default DcDropDown
